import ButtonLoading from 'components/ButtonLoading/ButtonLoading';
import Refresher from 'components/Refresher/Refresher';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import momentDiffHours from 'utils/momentDiffHours';

import ButtonBase from '@material-ui/core/ButtonBase';
import { makeStyles } from '@material-ui/core/styles';

import {
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Switch,
  Typography,
} from '@material-ui/core';
import {
  mdiAlertCircle,
  mdiArchiveOutline,
  mdiCalendarEndOutline,
  mdiChevronDown,
  mdiChevronUp,
  mdiClockTimeTwelveOutline,
  mdiEyeOffOutline,
  mdiEyeOutline,
  mdiFinance,
  mdiFormatSection,
  mdiLoginVariant,
  mdiMessageTextOutline,
  mdiPencil,
  mdiStar,
  mdiStarOutline,
  mdiStopCircleOutline,
} from '@mdi/js';
import Icon from '@mdi/react';
import { getChatControlData } from 'components/ChatDialog/store/slices/chatSlice';
import { openChatDialogForProject } from 'components/ChatDialog/store/thunks/openChatDialogForProject';
import OngoingCallIndicator from 'components/OngoingCallIndicator/OngoingCallIndicator';
import ProjectImage from 'components/ProjectDialog/components/ProjectImage';
import ProjectDialogRouter from 'components/ProjectDialog/ProjectDialog';
import SideBadge from 'components/SideBadge/SideBadge';
import TextInput from 'components/TextInput/TextInput';
import dayjs from 'dayjs';
import {
  EXPENSE_TRACKING_ACCESS,
  FILINGS_MANAGE,
  PROJECT_CHAT_ACCESS,
  PROJECT_EDIT,
} from 'scenes/Permissions/constants/permissions';
import { selectIsRequiredPermissionByProjectId } from 'scenes/Permissions/store/selectors/personalPermissionsSelectorsByProjectId';
import {
  setMobileShowList,
  setSelectedFolder,
} from 'scenes/Project/store/actions/projectActions';
import { setInitialProjectKeyToJoin } from 'store/projects/actions/projectJoin.actions';
import {
  isProjectEndDatePassed,
  isProjectEnded,
} from 'store/projects/utils/isProjectEnded';
import {
  PROJECTS_ACTIVITY_FILTER_MODES,
  selectProjectById,
  selectProjectPreferenceByProject,
  selectProjectsActivityFilterMode,
  selectSelectedProject,
} from 'store/projects/selectors/projectsSelectors';
import { selectUserEmployers } from 'store/projects/selectors/userProjects.selectors';
import { selectProject } from 'store/projects/thunks/selectProject.thunk';
import { userJoinProject } from 'store/projects/thunks/userJoinProject.thunk';
import { updateUserProjectPreference } from 'store/projects/thunks/updateUserProjectPreference.thunk';
import {
  selectClockInPendingProjectIds,
  selectPersonalClockedInWorkRecords,
} from 'store/records/selectors/records.selectors';
import { selectIsCurrentUserResidentOnlyInProject } from 'store/resources/selectors/resources.selectors';
import { clockInRecord } from 'store/records/thunks/clockInRecord.thunk';
import { openClockOutRecordDialog } from 'store/records/thunks/openClockOutRecordDialog';
import { openEditRecordDialog } from 'store/records/thunks/openEditRecordDialog.thunk';
import { selectLoggedInUser } from 'store/user/selectors.js/user.selectors';
import hoursToTime2 from 'utils/hoursToTime2';
import hoursToTimeSimple from 'utils/hoursToTimeSimple';
import isFeatureEnabled, { Feature } from 'utils/isFeatureEnabled';

const PROJECT_TITLE_SCROLL_SPEED = 10;
const PROJECT_TITLE_SCROLL_REFERENCE_WIDTH = 260;
const PROJECT_TITLE_SCROLL_END_PADDING = 16;

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(1),
  },
  buttonContainer: {
    ...theme.component.projectsListButton,
    borderRadius: theme.spacing(0.5),
    overflow: 'hidden',
  },
  buttonContainerSelected: {
    ...theme.component.projectsListButton,
    borderRadius: theme.spacing(0.5),
    overflow: 'hidden',
    // outline: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.primary.light,
    // boxShadow: `0 0 6px ${theme.palette.primary.light}40`,
  },
  firstButton: {
    padding: theme.spacing(1),
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    transition: 'background-color 0.3s',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    [theme.breakpoints.up('sm')]: {
      '&:hover $buttonTitle': {
        overflow: 'visible',
        '&.panning': {
          animationTimingFunction: 'linear',
          animationIterationCount: 'infinite',
          animationDuration: 'var(--animation-duration)',
          animationDirection: 'alternate',
          animationName: '$panTextHover',
        },
      },
    },
    textDecorationLine: 'none',
  },
  firstButtonDisabled: {
    padding: theme.spacing(1),
    cursor: 'default',
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    transition: 'background-color 0.3s',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    textDecorationLine: 'none',
    opacity: 0.5,
  },
  middlebutton: {
    padding: theme.spacing(0.5),
    display: 'flex',

    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    transition: 'background-color 0.3s',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  lastButton: {
    padding: theme.spacing(0.5),
    display: 'flex',

    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    transition: 'background-color 0.3s',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  buttonTitle: {
    ...theme.typography.fontPanton,
    fontSize: '0.9rem',
    maxWidth: '100%',
    overflowY: 'visible',
    overflowX: 'clip',
    whiteSpace: 'nowrap',
    position: 'relative',
    display: 'inline-block',
    textOverflow: 'ellipsis',
    animation: 'none' /* Default, no animation if text fits */,
    willChange: 'transform',

    [theme.breakpoints.down('xl')]: {
      overflow: 'visible',
      '&.panning': {
        animationTimingFunction: 'linear',
        animationIterationCount: 'infinite',
        animationDuration: 'var(--animation-duration)',
        animationDirection: 'alternate',
        animationName: '$panTextMobile',
        animationDelay: '1s',
      },
    },
  },
  '@keyframes panTextHover': {
    '0%': {
      transform: 'translateX(0)',
    },
    // '10%': {
    //   transform: 'translateX(0)',
    // },
    '90%': {
      transform: 'translateX(calc(-1 * var(--translate-distance) - 1rem))',
    },
    '100%': {
      transform: 'translateX(calc(-1 * var(--translate-distance) - 1rem))',
    },
  },
  '@keyframes panTextMobile': {
    '0%': {
      transform: 'translateX(0)',
    },
    '10%': {
      transform: 'translateX(0)',
    },
    '90%': {
      transform: 'translateX(calc(-1 * var(--translate-distance) - 1rem))',
    },
    '100%': {
      transform: 'translateX(calc(-1 * var(--translate-distance) - 1rem))',
    },
  },
  buttonSubtitle: {
    fontSize: '0.75rem',
    lineHeight: '1',
    maxWidth: '100%',
    overflowY: 'visible',
    overflowX: 'clip',
  },
  // Monospace digits so the running clock-out timer stays fixed-width and
  // doesn't jump as the numbers change (Panton has no tabular figures).
  clockOutTime: {
    fontFamily: "'Roboto Mono', 'Menlo', 'Consolas', monospace",
    fontVariantNumeric: 'tabular-nums',
    fontSize: '0.7rem',
    // On phone widths the seconds-bearing H:MM:SS overflows the narrow
    // clock cell, so shrink only there.
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.6rem',
    },
  },
  infoContainer: {
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(1),
  },
  info: {
    marginBottom: theme.spacing(1),
    display: 'flex',
  },
  infoText: {
    ...theme.typography.fontPanton,
    fontSize: '0.9rem',
  },
  infoTextWarning: {
    ...theme.typography.fontPanton,
    fontSize: '0.9rem',
    color: theme.palette.warning.main,
  },
  infoTextSubtitle: {
    fontSize: '0.75rem',
    lineHeight: '1',
  },
  rotatingClockIcon: {
    animation: 'spin 10s linear infinite',
  },
  '@global': {
    '@keyframes spin': {
      '0%': {
        transform: 'rotate(0deg)',
      },
      '100%': {
        transform: 'rotate(360deg)',
      },
    },
  },
  threadListItem: {
    alignItems: 'center',
  },
  ilmoitettavaIndicator: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: theme.palette.warning.main,
    display: 'inline-block',
    marginLeft: theme.spacing(0.75),
    verticalAlign: 'middle',
    flexShrink: 0,
  },
  ilmoitettavaWarningIcon: {
    display: 'inline-flex',
    marginLeft: theme.spacing(0.5),
    verticalAlign: 'middle',
    color: theme.palette.error.main,
  },
  titleIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    verticalAlign: 'middle',
    marginRight: theme.spacing(0.5),
  },
}));

function ProjectListItem({
  project,
  type = 'project',
  unjoined = false,
  resident = false,
  isSelected = false,
  hasTreeChildren = false,
  isTreeExpanded = false,
  onToggleTreeExpand,
  hideExpandButton = false,
  showRecordEditButton = false,
}) {
  const { t } = useTranslation('common');

  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const businessId = project?.business_id;

  const [clockedIn, setClockedIn] = useState(false);
  const [clockOutHover, setClockOutHover] = useState(false);
  const [open, setOpen] = useState(false);
  const [chatMenuOpen, setChatMenuOpen] = useState(false);
  const [editProjectDialogOpen, setEditProjectDialog] = useState(false);
  const [projectImage] = useState(project.picture || null);

  const inRecord = useSelector(selectPersonalClockedInWorkRecords).find(
    (record) => record.project.id === project.id
  );
  const isResidentOnlySelector = React.useMemo(
    () => selectIsCurrentUserResidentOnlyInProject(project.id),
    [project?.id]
  );
  const isJoinedAsResidentOnly = useSelector(isResidentOnlySelector);
  const projectPreferenceSelector = React.useMemo(
    () => selectProjectPreferenceByProject(project),
    [project?.business_id, project?.id]
  );
  const projectPreference = useSelector(projectPreferenceSelector);
  const projectFromState = useSelector(selectProjectById(project.id));
  const clockInPendingProjectIds = useSelector(selectClockInPendingProjectIds);
  const storeSelectedProject = useSelector(selectSelectedProject);
  const employers = useSelector(selectUserEmployers);
  const projectsActivityFilterMode = useSelector(
    selectProjectsActivityFilterMode
  );

  const isPermissionToAccessExpenseTracking = useSelector((state) =>
    selectIsRequiredPermissionByProjectId(project.id)(
      state,
      EXPENSE_TRACKING_ACCESS
    )
  );

  const isPermissionToAccessFilings = useSelector((state) =>
    selectIsRequiredPermissionByProjectId(project.id)(state, FILINGS_MANAGE)
  );

  const isPermissionToAccessProjectEdit = useSelector((state) =>
    selectIsRequiredPermissionByProjectId(project.id)(state, PROJECT_EDIT)
  );

  const isPermissionToAccessProjectChat = useSelector((state) =>
    selectIsRequiredPermissionByProjectId(project.id)(
      state,
      PROJECT_CHAT_ACCESS
    )
  );

  const projectMode = type === 'project';
  const folderMode = type === 'folder';
  const employerMode = type === 'employer';
  const isResident = resident || isJoinedAsResidentOnly;
  const showClockButton = projectMode && (!isResident || Boolean(inRecord));
  const isVerottajailmoitettavaFilterView =
    projectsActivityFilterMode ===
    PROJECTS_ACTIVITY_FILTER_MODES.VEROTTAJAILMOITETTAVA;
  const isVerottajailmoitettavaProject =
    project?.ilve === 'yes' ||
    project?.ilve === 'true' ||
    project?.ilve === true ||
    project?.ilmoitettava === true;

  // Whether the chevron / edit-record button slot (xs={2}) is rendered. When it
  // isn't (e.g. timer drawer with no active record), the name section takes its
  // place so the clock-in button stays at the end of the row.
  const showExpandOrEditButton = Boolean(
    (showRecordEditButton && clockedIn && inRecord) || !hideExpandButton
  );

  useEffect(() => {
    setClockedIn(inRecord ? true : false);
  }, [inRecord]);

  const handleClickProject = () => {
    if (unjoined) return;
    if (folderMode) {
      dispatch(
        setSelectedFolder({
          projectId: project.id,
          projectBusinessId: project.business_id,
        })
      );
    } else {
      dispatch(selectProject(project.id));

      if (projectMode && !hasTreeChildren) {
        dispatch(setMobileShowList(false));
      }
    }
  };

  const handleJoin = async (projectKey) => {
    // executed when only one employer exists for user, the only employer (from [0] index) is selected
    console.log('JOINING DIRECTLY WITH EMPLOYER OF ', employers[0]);
    if (employers.length > 1) {
      dispatch(setInitialProjectKeyToJoin(projectKey));
      history.push('/project/join');
    } else {
      dispatch(
        userJoinProject({
          projectKey,
          employerId:
            employers[0] && employers[0].id !== '0'
              ? employers[0].id
              : 'my-lixani',
        })
      ).then((response) => {
        if (response.payload) {
          history.replace(`/project/${projectKey}`);
        }
      });
    }
  };

  const clockOut = (record) => {
    dispatch(openClockOutRecordDialog(record.project.id));
  };

  const clockIn = async () => {
    dispatch(
      clockInRecord({
        projectId: project.id,
        allowClockInWithoutLocation: false,
      })
    );
  };
  const businessType = {
    customer: t('project.business_type_list.customer'),
    employer: t('project.business_type_list.employer'),
    owner: t('project.business_type_list.owner'),
    rental_employer: t('project.business_type_list.rental_employer'),
    normal: t('project.business_type_list.normal'),
    folder: t('project.business_type_list.folder'),
  };

  const clockInLoading = clockInPendingProjectIds[project.id];
  const isFavorite = Boolean(projectPreference?.favorite);
  const isHidden = Boolean(projectPreference?.hidden);
  const canToggleProjectPreference = !employerMode;

  const projectName = project?.name;

  const projectKey = project?.projectKey;
  const isNormalProject = project?.businessTypeEnum === 'NORMAL';
  const normalizedProjectStatus =
    !project?.status || project?.status === 'ACTIVE'
      ? 'IN_PROGRESS'
      : project?.status;
  const projectStatusTranslationKey =
    isNormalProject &&
    ['ARCHIVED', 'ESTIMATING', 'IN_PROGRESS', 'WARRANTY_PERIOD'].includes(
      normalizedProjectStatus
    )
      ? normalizedProjectStatus === 'IN_PROGRESS' &&
        isVerottajailmoitettavaProject
        ? 'IN_PROGRESS_REPORTABLE'
        : normalizedProjectStatus
      : null;
  const projectStatusText = isProjectEnded(project)
    ? t('project_dialog.project_status.ENDED')
    : projectStatusTranslationKey
      ? t(`project_dialog.project_status.${projectStatusTranslationKey}`)
      : null;
  const projectMasterParentDisplayName = project?.masterParent?.name?.trim();
  const projectMasterParentName =
    project?.business_id !== storeSelectedProject.business_id &&
    projectMasterParentDisplayName ? (
      <span>
        {' - '}
        {projectMasterParentDisplayName}
      </span>
    ) : null;
  const projectTypeName = (
    <span>
      {' - '}
      {businessType[project?.businessTypeEnum?.toLowerCase()]}
    </span>
  );
  const shortDescription = project?.shortDescription || '-';

  const firstButtonComponent = folderMode || unjoined ? 'button' : Link;

  const handleUpdatePreference = (nextPreference) => {
    if (!project?.id || !project?.business_id || !canToggleProjectPreference) {
      return;
    }

    dispatch(
      updateUserProjectPreference({
        project,
        ...nextPreference,
      })
    );
  };

  const handleToggleFavorite = () => {
    handleUpdatePreference({
      favorite: !isFavorite,
      hidden: isHidden,
    });
  };

  const handleToggleHidden = () => {
    handleUpdatePreference({
      favorite: isFavorite,
      hidden: !isHidden,
    });
  };

  const buttonRef = useRef(null);
  const textRef = useRef(null);
  const [translateDistance, setTranslateDistance] = useState(0);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [animationDuration, setAnimationDuration] = useState('0s');

  useEffect(() => {
    const measureTitleAnimation = () => {
      if (!buttonRef.current || !textRef.current) {
        return;
      }

      const buttonWidth = buttonRef.current.clientWidth;
      const textWidth = textRef.current.scrollWidth;

      if (textWidth <= buttonWidth) {
        setTranslateDistance(0);
        setShouldAnimate(false);
        setAnimationDuration('0s');
        return;
      }

      const overflowDistance = textWidth - buttonWidth;
      const widthRatio = Math.min(
        1,
        buttonWidth / PROJECT_TITLE_SCROLL_REFERENCE_WIDTH
      );
      const adjustedSpeedPerSecond =
        PROJECT_TITLE_SCROLL_SPEED * Math.max(widthRatio, 0.6);
      const totalDistance = overflowDistance + PROJECT_TITLE_SCROLL_END_PADDING;

      setTranslateDistance(overflowDistance);
      setShouldAnimate(true);
      setAnimationDuration(`${totalDistance / adjustedSpeedPerSecond}s`);
    };

    measureTitleAnimation();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measureTitleAnimation);

      return () => {
        window.removeEventListener('resize', measureTitleAnimation);
      };
    }

    const resizeObserver = new ResizeObserver(() => {
      measureTitleAnimation();
    });

    if (buttonRef.current) {
      resizeObserver.observe(buttonRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [projectName]);

  const { threadsByProject } = useSelector(getChatControlData);
  const thread = threadsByProject[project.id];
  const childThreads =
    thread?.children.filter((child) => child.unreadMessagesCount > 0) || [];
  const childThreadProjectIds = childThreads.map((thread) => thread.projectId);
  const childProjects = useSelector((state) =>
    childThreadProjectIds.reduce((acc, projectId) => {
      acc[projectId] = selectProjectById(projectId)(state);
      return acc;
    }, {})
  );
  const openChatDialog = (projectToOpen, openDialog) => {
    if (!openDialog && thread?.unreadMessagesInChildProjects > 0) {
      setChatMenuOpen(!chatMenuOpen);
    } else {
      dispatch(openChatDialogForProject({ project: projectToOpen }));
    }
  };

  const unreadChatMessagesCount =
    (thread?.unreadMessagesCount || 0) +
    (thread?.unreadMessagesInChildProjects || 0);

  const showChat =
    isFeatureEnabled(Feature.PROJECT_CHAT, project) &&
    isPermissionToAccessProjectChat &&
    (!employerMode || childThreads.length > 0);

  const validTaxNumberRequired = project?.validTaxNumberRequired || false;
  const taxNumberStatus =
    useSelector(selectLoggedInUser)?.taxNumberStatus || false;
  const userHasInvalidTaxNumber =
    validTaxNumberRequired && taxNumberStatus === 'INVALID';

  useEffect(() => {
    if (open) {
      console.log('PROJECT', project);
    }
  }, [open]);

  return (
    <>
      <div className={classes.root}>
        <Grid
          container
          direction="row"
          className={
            isSelected
              ? classes.buttonContainerSelected
              : classes.buttonContainer
          }
        >
          <Grid
            item
            xs={
              employerMode
                ? 10
                : projectMode
                ? !showClockButton
                  ? showExpandOrEditButton
                    ? 10
                    : 12
                  : showExpandOrEditButton
                  ? 8
                  : 10
                : 12
            }
          >
            <ButtonBase
              {...(!unjoined && { onClick: handleClickProject })}
              className={
                unjoined ? classes.firstButtonDisabled : classes.firstButton
              }
              to={
                !folderMode && !unjoined ? '/project/' + projectKey : undefined
              }
              component={firstButtonComponent}
              disableRipple={unjoined}
              ref={buttonRef}
            >
              <Typography
                ref={textRef}
                className={`${classes.buttonTitle} ${
                  shouldAnimate ? 'panning' : ''
                }`}
                style={{
                  '--translate-distance': `${translateDistance}px`,
                  animationDuration,
                }}
              >
                {isFavorite && (
                  <span className={classes.titleIcon}>
                    <Icon path={mdiStar} size={0.5} color="#FFD700" />
                  </span>
                )}
                {isHidden && (
                  <span className={classes.titleIcon}>
                    <Icon path={mdiEyeOffOutline} size={0.5} />
                  </span>
                )}
                {project?.status === 'ARCHIVED' && (
                  <span className={classes.titleIcon}>
                    <Icon path={mdiArchiveOutline} size={0.5} />
                  </span>
                )}
                {isProjectEndDatePassed(project) && (
                  <span className={classes.titleIcon}>
                    <Icon path={mdiCalendarEndOutline} size={0.5} />
                  </span>
                )}
                {projectName}
                {projectMode &&
                  isVerottajailmoitettavaFilterView &&
                  (isVerottajailmoitettavaProject ? (
                    <span
                      className={classes.ilmoitettavaIndicator}
                      title={t(
                        'project.reportable_project',
                        'Reportable project'
                      )}
                    />
                  ) : (
                    <span
                      className={classes.ilmoitettavaWarningIcon}
                      title={t(
                        'project.not_reportable_project',
                        'Not reportable project'
                      )}
                    >
                      <Icon path={mdiAlertCircle} size={0.6} />
                    </span>
                  ))}
              </Typography>
              <Typography className={classes.buttonSubtitle} noWrap>
                {projectMode ? projectKey : null}
                {projectMode && projectStatusText
                  ? ` - ${projectStatusText}`
                  : null}
                {projectMode ? projectMasterParentName : null}
                {employerMode || folderMode ? businessId : null}
                {employerMode || folderMode ? projectTypeName : null}
              </Typography>
              <Typography className={classes.buttonSubtitle} noWrap>
                {projectMode ? shortDescription : null}
              </Typography>
            </ButtonBase>
          </Grid>
          {!projectMode && !unjoined && !resident && showChat && (
            <Grid item xs={2}>
              <ButtonBase
                className={classes.middlebutton}
                onClick={() => openChatDialog(project, false)}
              >
                <SideBadge
                  badgeContent={unreadChatMessagesCount}
                  color="secondary"
                  invisible={unreadChatMessagesCount === 0}
                >
                  <Icon path={mdiMessageTextOutline} size={1} />
                </SideBadge>
              </ButtonBase>
            </Grid>
          )}
          {!unjoined && (
            <>
              {showRecordEditButton && clockedIn && inRecord ? (
                <Grid
                  item
                  xs={2}
                  justifyContent="center"
                  alignItems="center"
                  container
                >
                  <ButtonBase
                    onClick={() => dispatch(openEditRecordDialog(inRecord.id))}
                    className={classes.middlebutton}
                  >
                    <Icon path={mdiPencil} size={1} />
                  </ButtonBase>
                </Grid>
              ) : (
                !hideExpandButton && (
                  <Grid
                    item
                    xs={2}
                    justifyContent="center"
                    alignItems="center"
                    container
                  >
                    <ButtonBase
                      onClick={() => setOpen(!open)}
                      className={classes.middlebutton}
                    >
                      <Icon
                        path={open ? mdiChevronUp : mdiChevronDown}
                        size={1}
                      />
                    </ButtonBase>
                  </Grid>
                )
              )}
              {showClockButton && (
                <Grid
                  item
                  xs={2}
                  alignItems="center"
                  justifyContent="center"
                  container
                >
                  <ButtonBase
                    onClick={clockedIn ? () => clockOut(inRecord) : clockIn}
                    className={classes.lastButton}
                    onMouseEnter={() => setClockOutHover(true)}
                    onMouseLeave={() => setClockOutHover(false)}
                    disabled={
                      project.status === 'ARCHIVED' ||
                      userHasInvalidTaxNumber ||
                      (!clockedIn &&
                        project.preventClockInAfterEndDate !== false &&
                        isProjectEnded(project))
                    }
                  >
                    {clockedIn && inRecord ? (
                      <Refresher
                        render={() => {
                          let clockInTime = momentDiffHours(inRecord.clockInAt);
                          return (
                            <Grid
                              container
                              direction="column"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Icon
                                path={
                                  clockOutHover
                                    ? mdiStopCircleOutline
                                    : mdiClockTimeTwelveOutline
                                }
                                size={1}
                                className={
                                  clockOutHover
                                    ? undefined
                                    : classes.rotatingClockIcon
                                }
                              />
                              <Typography
                                className={`${classes.buttonSubtitle} ${classes.clockOutTime}`}
                                key={inRecord.id}
                              >
                                {Math.abs(clockInTime) >= 10
                                  ? hoursToTimeSimple(clockInTime)
                                  : hoursToTime2(clockInTime)}
                              </Typography>
                            </Grid>
                          );
                        }}
                      />
                    ) : clockInLoading ? (
                      <ButtonLoading />
                    ) : (
                      <Icon path={mdiLoginVariant} size={1} />
                    )}
                  </ButtonBase>
                </Grid>
              )}
            </>
          )}
          {unjoined && !resident && (
            <>
              <Grid
                item
                xs={4}
                alignItems="center"
                justifyContent="center"
                container
              >
                <ButtonBase
                  onClick={() => handleJoin(project.projectKey)}
                  className={classes.lastButton}
                >
                  <Typography className={classes.infoText}>
                    {t('dashboard.join_project')}
                  </Typography>
                </ButtonBase>
              </Grid>
            </>
          )}
          {/* Ongoing video call — a full-width strip rather than another
              action cell, because the row's 12 columns are already spoken for. */}
          {projectMode && !unjoined && (
            <Grid item xs={12}>
              <OngoingCallIndicator project={project} variant="row" />
            </Grid>
          )}
          {chatMenuOpen && (
            <Grid
              item
              xs={12}
              spacing={1}
              container
              className={classes.infoContainer}
            >
              {!employerMode && (
                <Grid item xs={12} container className={classes.threadListItem}>
                  <Grid item xs={10}>
                    {project.name}
                  </Grid>
                  <Grid item xs={2}>
                    <ButtonBase
                      className={classes.middlebutton}
                      onClick={() => openChatDialog(project, true)}
                    >
                      <SideBadge
                        badgeContent={thread.unreadMessagesCount}
                        color="secondary"
                        invisible={thread.unreadMessagesCount === 0}
                      >
                        <Icon path={mdiMessageTextOutline} size={1} />
                      </SideBadge>
                    </ButtonBase>
                  </Grid>
                </Grid>
              )}
              {childThreads.map((childThread) => (
                <Grid item xs={12} container key={childThread.id}>
                  <Grid item xs={10}>
                    {childThread.title}
                  </Grid>
                  <Grid item xs={2}>
                    <ButtonBase
                      className={classes.middlebutton}
                      onClick={() => {
                        const childProject =
                          childProjects[childThread.projectId];
                        openChatDialog(childProject, true);
                      }}
                    >
                      <SideBadge
                        badgeContent={childThread.unreadMessagesCount}
                        color="secondary"
                        invisible={false}
                      >
                        <Icon path={mdiMessageTextOutline} size={1} />
                      </SideBadge>
                    </ButtonBase>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          )}
          {open && (
            <Grid item xs={12} container className={classes.infoContainer}>
              <Grid
                item
                xs={projectImage ? 9 : 12}
                className={classes.info}
                container
                direction="column"
              >
                <Typography className={classes.infoText}>
                  {project.street1} {project.street2} {project.city}
                </Typography>
                {project.createdAt && (
                  <Typography className={classes.infoText}>
                    {t('project.created_at')}:{' '}
                    {dayjs(project.createdAt).format('DD.MM.YYYY')}
                  </Typography>
                )}
                {/* {project.ilmoitettava && isPermissionToAccessFilings && (
                  <Typography className={classes.infoText}>
                    Verottajailmoitettava:{' '}
                    <span
                      className={
                        project.ilmoitettava
                          ? classes.infoTextWarning
                          : classes.infoText
                      }
                    >
                      {project.ilmoitettava ? 'Kyllä' : 'Ei'}
                    </span>
                  </Typography>
                )} */}
              </Grid>
              {projectImage && (
                <Grid item xs={3} className={classes.info}>
                  <ProjectImage fileObject={projectImage} />
                </Grid>
              )}
              {project.longDescription && (
                <Grid item xs={12} className={classes.info}>
                  <Typography className={classes.infoText}>
                    {project.longDescription?.split('\n').map((line, index) => (
                      <React.Fragment key={index}>
                        {line}
                        <br />
                      </React.Fragment>
                    ))}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Divider />
              </Grid>
              {isNormalProject && isPermissionToAccessProjectEdit && (
                <Grid container item className={classes.info}>
                  <Grid item xs={12}>
                    <TextInput
                      fullWidth
                      select
                      label={t('project_dialog.field_project_status')}
                      value={normalizedProjectStatus}
                      onClick={() => setEditProjectDialog(true)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setEditProjectDialog(true);
                        }
                      }}
                      inputProps={{ readOnly: true }}
                      SelectProps={{
                        open: false,
                        onMouseDown: (e) => {
                          e.preventDefault();
                          setEditProjectDialog(true);
                        },
                        renderValue: () => projectStatusText || '',
                      }}
                    >
                      <MenuItem value="ESTIMATING">
                        {t('project_dialog.project_status.ESTIMATING')}
                      </MenuItem>
                      <MenuItem value="IN_PROGRESS">
                        {t('project_dialog.project_status.IN_PROGRESS')}
                      </MenuItem>
                      <MenuItem value="WARRANTY_PERIOD">
                        {t('project_dialog.project_status.WARRANTY_PERIOD')}
                      </MenuItem>
                      {normalizedProjectStatus === 'ARCHIVED' && (
                        <MenuItem value="ARCHIVED">
                          {t('project_dialog.project_status.ARCHIVED')}
                        </MenuItem>
                      )}
                    </TextInput>
                  </Grid>
                </Grid>
              )}
              {isNormalProject &&
                isPermissionToAccessFilings &&
                isPermissionToAccessProjectEdit && (
                  <Grid container item>
                    <Grid item xs={8} alignItems="center" container>
                      <Typography>
                        {t(
                          'project_dialog.toggle_reportable',
                          'Subject to reporting'
                        )}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={4}
                      container
                      justifyContent="flex-end"
                      alignItems="center"
                    >
                      <Switch
                        checked={project.ilmoitettava}
                        onChange={(e) => setEditProjectDialog(true)}
                        inputProps={{ 'aria-label': 'controlled' }}
                      />
                    </Grid>
                  </Grid>
                )}

              <Grid
                item
                xs={12}
                className={classes.info}
                justifyContent="space-between"
                container
              >
                <Grid>
                  {isPermissionToAccessExpenseTracking ? (
                    <IconButton
                      to={`/project/${project.projectKey}/expensetracking/${project.id}`}
                      component={Link}
                    >
                      <Icon path={mdiFinance} size={1} />
                    </IconButton>
                  ) : null}
                  {isPermissionToAccessFilings &&
                  project.ilmoitettava === true ? (
                    <IconButton
                      to={`/project/${project.projectKey}/filings/${project.id}`}
                      component={Link}
                    >
                      <Icon path={mdiFormatSection} size={1} />
                    </IconButton>
                  ) : null}
                  {showChat && projectMode && (
                    <SideBadge
                      badgeContent={unreadChatMessagesCount}
                      color="secondary"
                      invisible={unreadChatMessagesCount === 0}
                    >
                      <IconButton
                        onClick={() => openChatDialog(project, false)}
                      >
                        <Icon path={mdiMessageTextOutline} size={1} />
                      </IconButton>
                    </SideBadge>
                  )}
                </Grid>
                <Grid>
                  {isPermissionToAccessProjectEdit ? (
                    <IconButton
                      aria-label={t('common.edit', 'Edit')}
                      onClick={() => setEditProjectDialog(true)}
                    >
                      <Icon path={mdiPencil} size={1} />
                    </IconButton>
                  ) : null}
                  {canToggleProjectPreference ? (
                    <>
                      <IconButton
                        aria-label={
                          isHidden
                            ? t('project.show_project')
                            : t('project.hide_project')
                        }
                        title={
                          isHidden
                            ? t('project.show_project')
                            : t('project.hide_project')
                        }
                        onClick={handleToggleHidden}
                      >
                        <Icon
                          path={isHidden ? mdiEyeOutline : mdiEyeOffOutline}
                          size={1}
                        />
                      </IconButton>
                      <IconButton
                        aria-label={
                          isFavorite
                            ? t('project.unfavorite_project')
                            : t('project.favorite_project')
                        }
                        title={
                          isFavorite
                            ? t('project.unfavorite_project')
                            : t('project.favorite_project')
                        }
                        onClick={handleToggleFavorite}
                      >
                        <Icon
                          path={isFavorite ? mdiStar : mdiStarOutline}
                          size={1}
                          color={isFavorite ? '#FFD700' : undefined}
                        />
                      </IconButton>
                    </>
                  ) : null}
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </div>
      {editProjectDialogOpen && (
        <ProjectDialogRouter
          onClose={() => setEditProjectDialog(false)}
          open={editProjectDialogOpen}
          type="edit"
          redirect={false}
          project={projectFromState}
        />
      )}
    </>
  );
}

ProjectListItem.propTypes = {
  project: PropTypes.object.isRequired,
  type: PropTypes.oneOf(['project', 'folder', 'employer']).isRequired,
  unjoined: PropTypes.bool, // True if project hasnt been joined
  resident: PropTypes.bool, // True if project is joined as resident
  isSelected: PropTypes.bool, // True if this is the currently selected project in tree view
  hasTreeChildren: PropTypes.bool, // True if this project has children in the tree
  isTreeExpanded: PropTypes.bool, // True if this project's children are shown in the tree
  onToggleTreeExpand: PropTypes.func, // Callback to toggle tree expand/collapse
  hideExpandButton: PropTypes.bool, // Hide the expand/collapse chevron (e.g. timer drawer)
  showRecordEditButton: PropTypes.bool, // When clocked in, show edit-record button in the chevron's place
};

export default ProjectListItem;
